import { useEffect, useState } from 'react';
import {
  Alert,
  AutoComplete,
  Button,
  Form,
  Image,
  Input,
  Modal,
  Space,
  Steps,
  Upload,
  notification,
} from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import CustomButton from 'components/custom-button';
import { imageUrl } from 'pages/star-shared/utils';
import type { StudioCandidate, StudioPerson, StudioSummary } from '../contract';
import {
  CreateStudioBody,
  EditStudioBody,
  createStudio,
  editStudio,
  getAgencyGroups,
  uploadStudioImage,
} from '../api';
import { mapStudioError, studioErrorCode } from '../studio-error';
import { REASON_MAX, REASON_MIN, TIKTOK_GROUP_HINT } from '../labels';
import MemberPicker, { candidatesToPayload } from '../components/member-picker';
import { ManagerSelect } from './change-manager-modal';

export interface StudioFormModalProps {
  open: boolean;
  /** Có: sửa Studio (S6) — thông tin bước 1 + Trợ lý. Không: tạo Studio 3 bước (S4). */
  studio?: StudioSummary | null;
  onClose: () => void;
  onDone?: (studio: StudioSummary) => void;
}

interface FormValues {
  title: string;
  description?: string;
  avatar?: string | null;
  tiktokGroupName?: string;
  managerId?: number;
  assistants?: StudioCandidate[];
  members?: StudioCandidate[];
  reason?: string;
}

const TITLE_MAX = 200;
const DESCRIPTION_MAX = 300;
const TIKTOK_NAME_MAX = 120;
const ASSISTANTS_MAX = 10;
const MAX_IMAGE_MB = 5;

/** Trường của từng bước (tạo Studio): "Tiếp tục" chỉ kiểm trường của bước đang mở. */
const STEP_FIELDS: (keyof FormValues)[][] = [
  ['title', 'description', 'avatar', 'tiktokGroupName'],
  ['managerId', 'assistants'],
  ['members'],
];
const STEP_TITLES = ['Thông tin', 'Quản lý', 'Thành viên'];

/** Lỗi máy chủ thuộc bước nào (để mở lại đúng bước khi tạo Studio lỗi). */
function stepOfError(code: string | null): number | null {
  if (!code) return null;
  if (code === 'STUDIO_TITLE_EXISTS' || code === 'STUDIO_TIKTOK_NAME_TAKEN') return 0;
  if (code.startsWith('STUDIO_MANAGER_')) return 1;
  if (code === 'STUDIO_MEMBER_INVALID' || code === 'STUDIO_UID_NOT_FOUND') return 2;
  return null;
}

/** Trợ lý đang có (StudioPerson) → giá trị của ô chọn (StudioCandidate, khoá 'user:<id>' như S26). */
function personToCandidate(p: StudioPerson): StudioCandidate {
  return {
    key: `user:${p.id}`,
    userId: p.id,
    uid: null,
    name: p.nickName,
    username: p.username,
    avatar: p.avatar,
    hasAccount: true,
    currentStudio: null,
    inThisStudio: false,
    selectable: true,
    reason: null,
  };
}

const sortedIds = (ids: number[]) => [...ids].sort((x, y) => x - y).join(',');
const sameIds = (a: number[], b: number[]) => sortedIds(a) === sortedIds(b);
/** Bỏ id trùng: một người có thể hiện dưới hai khoá ('user:<id>' và 'uid:<uid>') nên ô chọn không tự khử. */
function unique<T>(list: T[]): T[] {
  return Array.from(new Set(list));
}

/** Ứng viên có tài khoản đã nằm trong danh sách đang chọn (dưới khoá khác) thì không chọn thêm được. */
const pickedAccount = (picked: StudioCandidate[], c: StudioCandidate) =>
  c.userId !== null && c.userId !== undefined && picked.some((p) => p.userId === c.userId);

// ------------------------------------------------------------ ô ảnh đại diện ---

interface AvatarFieldProps {
  value?: string | null;
  onChange?: (value: string | null) => void;
}

/** Ảnh đại diện: tải lên hạ tầng sẵn có (API.uploadFile), giá trị là khoá 'hp/<uuid>' mà S4 / S6 nhận. */
const AvatarField = ({ value, onChange }: AvatarFieldProps) => {
  const [uploading, setUploading] = useState(false);

  const beforeUpload = (file: File) => {
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      notification.error({ message: 'Chỉ nhận ảnh PNG, JPG hoặc WEBP.' });
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      notification.error({ message: `Ảnh không được lớn hơn ${MAX_IMAGE_MB} MB.` });
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  return (
    <Space align="start" wrap>
      {value && <Image src={imageUrl(value)} width={72} height={72} style={{ objectFit: 'cover' }} />}
      <Space direction="vertical">
        <Upload
          accept="image/png,image/jpeg,image/webp"
          showUploadList={false}
          beforeUpload={beforeUpload}
          customRequest={async ({ file, onSuccess, onError }) => {
            setUploading(true);
            try {
              const key = await uploadStudioImage(file as File);
              onChange?.(key);
              onSuccess?.(key);
            } catch (err) {
              notification.error({ message: mapStudioError(err) });
              onError?.(err as Error);
            } finally {
              setUploading(false);
            }
          }}
        >
          <Button icon={<UploadOutlined />} loading={uploading}>
            {value ? 'Đổi ảnh' : 'Tải ảnh lên'}
          </Button>
        </Upload>
        {value && (
          <Button icon={<DeleteOutlined />} onClick={() => onChange?.(null)} disabled={uploading}>
            Xoá ảnh
          </Button>
        )}
      </Space>
    </Space>
  );
};

// ------------------------------------------------------------------ modal ---

/**
 * Tạo Studio (S4, mục 8.7.1) theo 3 bước (antd Steps): Thông tin (tên, mô tả, ảnh, "Nhóm trong file TikTok"
 * gợi ý từ /revenues/agency-groups) → Quản lý (S3) + Trợ lý (tuỳ chọn, ≤ 10) → Thành viên (tuỳ chọn, ô chọn
 * S26: cả tài khoản lẫn Creator chỉ có UID, ≤ 100 mỗi loại). Sửa Studio (S6): các trường bước 1 + Trợ lý, chỉ
 * gửi trường đã đổi; đổi "Nhóm trong file TikTok" thì bắt buộc lý do. Lỗi hiện đúng câu máy chủ trả.
 */
const StudioFormModal = ({ open, studio, onClose, onDone }: StudioFormModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groupNames, setGroupNames] = useState<string[]>([]);
  const editing = !!studio;
  const managerId = Form.useWatch('managerId', form);
  const members = Form.useWatch('members', form) || [];
  const assistants = Form.useWatch('assistants', form) || [];
  const tiktokGroupName = Form.useWatch('tiktokGroupName', form);
  const memberCounts = candidatesToPayload(members);
  const groupNameChanged =
    editing && (tiktokGroupName ?? '').trim() !== (studio?.tiktokGroupName ?? '').trim();
  const currentManagerId = editing ? studio?.manager?.id ?? null : managerId ?? null;

  // Gợi ý tên nhóm trong file TikTok: chỉ Admin gọi được; lỗi thì bỏ gợi ý, vẫn gõ tay được.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    getAgencyGroups()
      .then((names) => {
        if (!cancelled) setGroupNames(Array.isArray(names) ? names : []);
      })
      .catch(() => {
        if (!cancelled) setGroupNames([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleClose = () => {
    if (saving) return;
    form.resetFields();
    setStep(0);
    setError(null);
    onClose();
  };

  const finish = (row: StudioSummary, message: string) => {
    notification.success({ message });
    form.resetFields();
    setStep(0);
    onDone?.(row);
    onClose();
  };

  const submitCreate = async (values: FormValues) => {
    const picked = candidatesToPayload(values.members || []);
    const memberUserIds = unique(picked.userIds);
    const memberUids = unique(picked.uids);
    const assistantIds = unique(candidatesToPayload(values.assistants || []).userIds);
    const title = values.title.trim();
    const body: CreateStudioBody = { title };
    // Quản lý không bắt buộc: bỏ trống → Studio chưa có Quản lý, gán sau bằng "Đổi Quản lý".
    if (values.managerId) body.managerId = values.managerId;
    const description = values.description?.trim();
    const groupName = values.tiktokGroupName?.trim();
    if (description) body.description = description;
    if (values.avatar) body.avatar = values.avatar;
    if (groupName) body.tiktokGroupName = groupName;
    if (assistantIds.length) body.assistantIds = assistantIds;
    if (memberUserIds.length) body.memberUserIds = memberUserIds;
    if (memberUids.length) body.memberUids = memberUids;
    const created = await createStudio(body);
    const added = created?.addedMembers ? `, thêm ${created.addedMembers} thành viên` : '';
    finish(created, `Đã tạo Studio "${created?.title || title}"${added}.`);
  };

  const submitEdit = async (values: FormValues) => {
    if (!studio) return;
    const body: EditStudioBody = {};
    const title = values.title.trim();
    const description = values.description?.trim() || '';
    const avatar = values.avatar || null;
    const groupName = values.tiktokGroupName?.trim() || '';
    const assistantIds = unique(candidatesToPayload(values.assistants || []).userIds);
    if (title !== studio.title) body.title = title;
    if (description !== (studio.description || '')) body.description = description;
    if (avatar !== (studio.avatar || null)) body.avatar = avatar || '';
    if (groupName !== (studio.tiktokGroupName || '').trim()) {
      body.tiktokGroupName = groupName || null;
      body.reason = values.reason?.trim();
    }
    if (!sameIds(assistantIds, studio.assistants.map((a) => a.id))) body.assistantIds = assistantIds;
    if (!Object.keys(body).length) {
      notification.info({ message: 'Chưa có thay đổi.' });
      return;
    }
    const row = await editStudio(studio.id, body);
    finish(row, `Đã lưu Studio "${row?.title || title}".`);
  };

  const onFinish = async (values: FormValues) => {
    setSaving(true);
    setError(null);
    try {
      if (editing) await submitEdit(values);
      else await submitCreate(values);
    } catch (err) {
      setError(mapStudioError(err));
      const at = editing ? null : stepOfError(studioErrorCode(err));
      if (at !== null) setStep(at);
    } finally {
      setSaving(false);
    }
  };

  // Tạo Studio: trường lỗi nằm ở bước trước thì mở lại bước đó.
  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[] }[] }) => {
    if (editing || !errorFields.length) return;
    const field = errorFields[0].name[0] as keyof FormValues;
    const at = STEP_FIELDS.findIndex((fields) => fields.includes(field));
    if (at >= 0) setStep(at);
  };

  const next = async () => {
    try {
      await form.validateFields(STEP_FIELDS[step]);
      setStep((s) => Math.min(s + 1, STEP_FIELDS.length - 1));
    } catch {
      // antd hiện lỗi dưới từng ô.
    }
  };

  // Bước đang ẩn: giữ Form.Item (hidden) để giữ giá trị và vẫn kiểm khi bấm "Tạo Studio".
  const show = (at: number) => (editing ? at !== 2 : step === at);
  const lastStep = editing || step === STEP_FIELDS.length - 1;

  const assistantDisabled = (c: StudioCandidate): string | null => {
    if (c.userId === null || c.userId === undefined) return 'Trợ lý phải có tài khoản app.';
    if (currentManagerId && c.userId === currentManagerId) return 'Người này là Quản lý của Studio.';
    if (pickedAccount(assistants, c)) return 'Đã chọn người này.';
    return null;
  };
  const memberDisabled = (c: StudioCandidate): string | null => {
    if (managerId && c.userId === managerId) return 'Người này là Quản lý của Studio.';
    if (pickedAccount(members, c)) return 'Đã chọn người này.';
    return c.selectable ? null : c.reason || 'Không chọn được người này.';
  };

  const initialValues: Partial<FormValues> = studio
    ? {
        title: studio.title,
        description: studio.description ?? '',
        avatar: studio.avatar ?? null,
        tiktokGroupName: studio.tiktokGroupName ?? '',
        assistants: studio.assistants.map(personToCandidate),
      }
    : { title: '', description: '', avatar: null, tiktokGroupName: '', assistants: [], members: [] };

  return (
    <Modal
      title={editing ? `Sửa Studio · ${studio?.title}` : 'Tạo Studio'}
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      maskClosable={false}
      width={680}
    >
      {!editing && (
        <Steps
          size="small"
          current={step}
          items={STEP_TITLES.map((title) => ({ title }))}
          style={{ marginBottom: 20 }}
        />
      )}
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      {/*
        destroyOnHidden + clearOnDestroy: đóng modal thì xoá sạch store của `form`. Không có clearOnDestroy,
        rc-field-form khi dựng lại lấy `merge(initialValues, store cũ)` → giá trị cũ (Studio sửa trước, tên trước
        khi lưu) thắng initialValues mới. Nơi dựng sẵn modal (tab Danh sách) còn đặt `key` theo lượt mở.
      */}
      <Form
        form={form}
        layout="vertical"
        className="custom-form"
        clearOnDestroy
        initialValues={initialValues}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        <Form.Item
          hidden={!show(0)}
          label="Tên Studio"
          name="title"
          rules={[
            { required: true, whitespace: true, message: 'Nhập tên Studio.' },
            { max: TITLE_MAX, message: `Tên tối đa ${TITLE_MAX} ký tự.` },
          ]}
        >
          <Input maxLength={TITLE_MAX} placeholder="VD: HT Team" />
        </Form.Item>
        <Form.Item
          hidden={!show(0)}
          label="Mô tả (không bắt buộc)"
          name="description"
          rules={[{ max: DESCRIPTION_MAX, message: `Mô tả tối đa ${DESCRIPTION_MAX} ký tự.` }]}
        >
          <Input.TextArea rows={2} maxLength={DESCRIPTION_MAX} showCount />
        </Form.Item>
        <Form.Item hidden={!show(0)} label="Ảnh đại diện (không bắt buộc)" name="avatar">
          <AvatarField />
        </Form.Item>
        <Form.Item
          hidden={!show(0)}
          label="Nhóm trong file TikTok (không bắt buộc)"
          name="tiktokGroupName"
          extra={TIKTOK_GROUP_HINT}
          rules={[{ max: TIKTOK_NAME_MAX, message: `Tối đa ${TIKTOK_NAME_MAX} ký tự.` }]}
        >
          <AutoComplete
            allowClear
            options={groupNames.map((name) => ({ value: name }))}
            filterOption={(input, option) =>
              String(option?.value || '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            placeholder="VD: HT Agency | 12/2021"
            maxLength={TIKTOK_NAME_MAX}
          />
        </Form.Item>
        {editing && groupNameChanged && (
          <Form.Item
            label="Lý do đổi Nhóm trong file TikTok"
            name="reason"
            extra="Đổi tên này là đổi danh sách Creator tự vào Studio theo file và tổng kim cương."
            rules={[
              {
                validator: (_, value?: string) =>
                  (value || '').trim().length >= REASON_MIN
                    ? Promise.resolve()
                    : Promise.reject(new Error(`Lý do cần ít nhất ${REASON_MIN} ký tự.`)),
              },
            ]}
          >
            <Input.TextArea rows={2} maxLength={REASON_MAX} showCount placeholder="Nhập lý do" />
          </Form.Item>
        )}
        {!editing && (
          <Form.Item
            hidden={!show(1)}
            label="Quản lý (không bắt buộc)"
            name="managerId"
            extra="Tài khoản MOD hoặc Agency. Người đang quản lý Studio khác không chọn được. Có thể bỏ trống và gán sau."
          >
            <ManagerSelect />
          </Form.Item>
        )}
        <Form.Item
          hidden={!show(1)}
          label={`Trợ lý (không bắt buộc, tối đa ${ASSISTANTS_MAX})`}
          name="assistants"
          extra="Tìm theo tên hoặc TikTok ID. Trợ lý phải có tài khoản app."
        >
          <MemberPicker multiple maxPerKind={ASSISTANTS_MAX} disabledReason={assistantDisabled} />
        </Form.Item>
        {!editing && (
          <Form.Item
            hidden={!show(2)}
            label="Thành viên (không bắt buộc)"
            name="members"
            extra={`Chọn được cả Creator chưa có tài khoản app (tối đa 100 mỗi loại). Người đang ở Studio khác sẽ được chuyển sang Studio này. Có thể bỏ qua và thêm sau. Đã chọn ${memberCounts.userIds.length} tài khoản, ${memberCounts.uids.length} Creator chưa có tài khoản.`}
          >
            <MemberPicker multiple disabledReason={memberDisabled} />
          </Form.Item>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          {!editing && step > 0 && (
            <CustomButton
              text="Quay lại"
              bgColor="third-green"
              onClick={() => setStep((s) => Math.max(s - 1, 0))}
              disabled={saving}
            />
          )}
          {lastStep ? (
            <CustomButton
              text={editing ? 'Lưu' : 'Tạo Studio'}
              bgColor="secondary-green"
              htmlType="submit"
              loading={saving}
            />
          ) : (
            <CustomButton text="Tiếp tục" bgColor="secondary-green" onClick={next} />
          )}
        </div>
      </Form>
    </Modal>
  );
};

export default StudioFormModal;
